import {
  BUSINESS,
  OFFER_TERMS,
  PRODUCT_IMAGE_SOURCES,
  PRODUCT_ITEM_CONDITION,
  PRODUCT_SHARE_IMAGE_SOURCE,
} from "../config/business";
import imageQuarantine from "../../../server/merchant-image-quarantine.json";
import { PRODUCT_GALLERY_IMAGE_LIMIT } from "./productCache";
import { productMediaShare } from "./productMedia";
import { resolveProductPrice } from "./productPrice";
import { isPublishableSku } from "./productSku";
import { finalSaleState, type MerchantFact } from "./productSeoFacts";
import type {
  ProductAttributeSnapshot,
  ProductReviewSnapshot,
  ProductSnapshot,
} from "../types/productSnapshot";

// Helpers for building schema.org structured data (JSON-LD) from store data.
//
// These are intentionally defensive: every field is optional and omitted when
// the source data is missing, so we never emit empty or fabricated values
// (e.g. a zero-review aggregateRating, which search engines penalize).

// "$329.00" -> "329.00"; "1,299.00" -> "1299.00". Returns null if no number.
export function parsePriceAmount(raw: unknown): string | null {
  if (raw == null) return null;
  const str = String(raw);
  const match = str.replace(/,/g, "").match(/\d+(\.\d+)?/);
  return match ? match[0] : null;
}

export function stripHtml(input: unknown): string {
  const decoded = String(input ?? "")
    .replace(/&#x([0-9a-f]+);?/gi, (_match, code) =>
      String.fromCodePoint(parseInt(code, 16))
    )
    .replace(/&#([0-9]+);?/g, (_match, code) =>
      String.fromCodePoint(parseInt(code, 10))
    )
    .replace(/&(nbsp|amp|quot|apos|lt|gt);/gi, (_match, entity) => {
      const entities: Record<string, string> = {
        nbsp: " ",
        amp: "&",
        quot: '"',
        apos: "'",
        lt: "<",
        gt: ">",
      };
      return entities[String(entity).toLowerCase()] || " ";
    });
  return decoded
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizedFieldName(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function fieldValue(value: unknown): string | undefined {
  const values = Array.isArray(value) ? value : [value];
  const result = values
    .map((item) => String(item ?? "").trim())
    .filter(Boolean)
    .join(", ");
  return result || undefined;
}

function productField(
  product: ProductSnapshot,
  aliases: string[]
): string | undefined {
  const names = new Set(aliases.map(normalizedFieldName));
  const attribute = Array.isArray(product.attributes)
    ? product.attributes.find((item: ProductAttributeSnapshot) =>
        names.has(normalizedFieldName(item?.name))
      )
    : undefined;
  const fromAttribute = fieldValue(attribute?.options ?? attribute?.value);
  if (fromAttribute) return fromAttribute;

  const extra = Array.isArray(product.extra)
    ? product.extra.find((item: ProductAttributeSnapshot) =>
        names.has(normalizedFieldName(item?.name))
      )
    : undefined;
  return fieldValue(extra?.value ?? extra?.options);
}

function verifiedMerchant(product: ProductSnapshot): MerchantFact | null {
  const merchant = product.merchant;
  return merchant && ["READY", "PARTIAL"].includes(merchant.status)
    ? merchant
    : null;
}

function productGtin(
  product: ProductSnapshot
): { key: string; value: string } | null {
  const merchant = verifiedMerchant(product);
  if (!merchant || merchant.gtinStatus !== "VALID") return null;
  const digits = String(merchant.gtin || "").replace(/\D/g, "");
  const keyByLength: Record<number, string> = {
    8: "gtin8",
    12: "gtin12",
    13: "gtin13",
    14: "gtin14",
  };
  const key = keyByLength[digits.length];
  return key ? { key, value: digits } : null;
}

function productMpn(product: ProductSnapshot): string | undefined {
  const merchant = verifiedMerchant(product);
  if (!merchant || merchant.mpnStatus !== "VALID") return undefined;
  return fieldValue(merchant.nativeMpn);
}

const trustedProductImageSources = PRODUCT_IMAGE_SOURCES.map((source) => ({
  origin: new URL(source.origin).origin,
  pathPrefix: source.pathPrefix,
}));

function admittedImage(value: unknown): string | null {
  try {
    const parsed = new URL(String(value || ""));
    if (parsed.protocol !== "https:") {
      return null;
    }
    const trustedSource = trustedProductImageSources.find(
      (source) =>
        source.origin === parsed.origin &&
        parsed.pathname.startsWith(source.pathPrefix)
    );
    if (!trustedSource) return null;
    parsed.hash = "";
    if (imageQuarantine.paths.includes(parsed.pathname)) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

const shareImageOrigin = new URL(PRODUCT_SHARE_IMAGE_SOURCE.origin).origin;

/** A slot's `share.jpg` from the renditions tree of the CDN, verbatim, or null. */
function admittedShare(media: unknown): string | null {
  const share = productMediaShare(media);
  if (!share) return null;
  try {
    const parsed = new URL(share.src);
    return parsed.protocol === "https:" &&
      parsed.origin === shareImageOrigin &&
      parsed.pathname.startsWith(PRODUCT_SHARE_IMAGE_SOURCE.pathPrefix) &&
      !parsed.search &&
      !parsed.hash
      ? share.src
      : null;
  } catch {
    return null;
  }
}

/**
 * The images one photo slot (the main photo or one gallery row) publishes:
 * its `share.jpg` when the slot's own legacy photo is admitted (trusted
 * source, not quarantined), else the admitted legacy URLs as before phase 7.
 * `share` never admits a slot that its legacy photo would not.
 */
function slotImages(
  slot:
    | {
        image?: unknown;
        imageBig?: unknown;
        media?: unknown;
      }
    | null
    | undefined
): string[] {
  const legacy = [slot?.imageBig, slot?.image].filter(
    (value) => typeof value === "string" && value.trim() !== ""
  );
  const admitted = legacy.map(admittedImage);
  if (!admitted.length || admitted.some((url) => !url)) {
    return admitted.filter((url): url is string => Boolean(url));
  }
  const share = admittedShare(slot?.media);
  return share ? [share] : (admitted as string[]);
}

function finalSaleTruth(product: ProductSnapshot): "TRUE" | "FALSE" | null {
  const state = finalSaleState(product.finalSale);
  return state === "UNKNOWN" ? null : state;
}

type ShippingOption =
  (typeof OFFER_TERMS.shippingOptions)[keyof typeof OFFER_TERMS.shippingOptions];

function shippingDetails(option: ShippingOption) {
  return {
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: option.rate,
      currency: OFFER_TERMS.currency,
    },
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: option.country,
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: {
        "@type": "QuantitativeValue",
        minValue: option.handlingDays.min,
        maxValue: option.handlingDays.max,
        unitCode: "DAY",
      },
      transitTime: {
        "@type": "QuantitativeValue",
        minValue: option.transitDays.min,
        maxValue: option.transitDays.max,
        unitCode: "DAY",
      },
    },
  };
}

function dimensionValue(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.replace(/\s*mm\b/gi, "").trim();
  return normalized || undefined;
}

/**
 * Build a schema.org Product object from a VueFront product and its canonical
 * URL. Offer uses the special (sale) price when present, otherwise the regular
 * price. AggregateRating/review are only included when real data exists.
 */
export function buildProductJsonLd(
  product: ProductSnapshot | null | undefined,
  canonicalUrl: string,
  brandFallback?: string
): Record<string, any> | null {
  if (!product || !product.id) return null;

  const priceSource = resolveProductPrice(product.special, product.price);
  const amount = parsePriceAmount(priceSource);

  const galleryImages = Array.isArray(product.images)
    ? product.images.flatMap(slotImages)
    : [];
  const images = Array.from(
    new Set([...slotImages(product), ...galleryImages])
  ).slice(0, PRODUCT_GALLERY_IMAGE_LIMIT);

  const description =
    stripHtml(product.shortDescription) ||
    stripHtml(product.description) ||
    undefined;

  const node: Record<string, any> = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name || product.model || "",
    url: canonicalUrl,
  };

  if (images.length) node.image = images;
  if (description) node.description = description.slice(0, 5000);
  // A null or zero SKU is no fact (utils/productSku): omit it.
  if (isPublishableSku(product.sku)) node.sku = String(product.sku).trim();
  const mpn = productMpn(product);
  if (mpn) node.mpn = mpn;
  const gtin = productGtin(product);
  if (gtin) node[gtin.key] = gtin.value;

  const brandName = product.manufacturer?.name || brandFallback;
  if (brandName) node.brand = { "@type": "Brand", name: brandName };

  const color = productField(product, [
    "Frame Color",
    "Frame Colour",
    "Color",
    "Colour",
  ]);
  const material = productField(product, ["Frame Material", "Material"]);
  const category = productField(product, ["Product Type", "Type"]);
  const declaredSize = productField(product, ["Size"]);
  const lensWidth = dimensionValue(productField(product, ["Lens Width"]));
  const bridgeWidth = dimensionValue(productField(product, ["Bridge Width"]));
  const templeWidth = dimensionValue(
    productField(product, ["Temple Width", "Temple Length"])
  );
  const composedSize =
    declaredSize ||
    ([lensWidth, bridgeWidth, templeWidth].every(Boolean)
      ? declaredSize || `${lensWidth} × ${bridgeWidth} × ${templeWidth} mm`
      : undefined);

  if (color) node.color = color;
  if (material) node.material = material;
  if (category) node.category = category;
  if (composedSize) node.size = composedSize;

  const additionalAliases: Array<[string, string[]]> = [
    ["Color code", ["Color Code", "Colour Code"]],
    ["Frame shape", ["Frame Shape", "Shape"]],
    ["Lens type", ["Lens Type"]],
    ["Lens color", ["Lens Color", "Lens Colour"]],
    ["Polarized", ["Polarized", "Polarised"]],
    ["Prescription ready", ["RX-able", "RX able", "Prescription Ready"]],
    ["Country of origin", ["Made In", "Country of Origin"]],
  ];
  const additionalProperty = additionalAliases
    .map(([name, aliases]) => ({
      "@type": "PropertyValue",
      name,
      value: productField(product, aliases),
    }))
    .filter((property) => property.value);
  if (additionalProperty.length) node.additionalProperty = additionalProperty;

  const merchantErrors = Array.isArray(product.merchant?.errors)
    ? product.merchant.errors
    : [];
  const isUnavailable =
    product.status === false || merchantErrors.includes("PRODUCT_UNAVAILABLE");
  if (amount && !isUnavailable) {
    const finalSale = finalSaleTruth(product);
    const offer: Record<string, any> = {
      "@type": "Offer",
      url: canonicalUrl,
      priceCurrency: OFFER_TERMS.currency,
      price: amount,
      availability:
        product.stock === true
          ? product.low_stock === true
            ? "https://schema.org/LimitedAvailability"
            : "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: PRODUCT_ITEM_CONDITION,
      // Handling plus transit matches each published delivery window.
    };

    // Policies are facts only when the typed bridge record is definitive.
    if (finalSale) {
      // Google product markup: exactly the two published shipping options.
      offer.shippingDetails = [
        shippingDetails(OFFER_TERMS.shippingOptions.standardUs),
        shippingDetails(OFFER_TERMS.shippingOptions.expressUs),
      ];
      offer.hasMerchantReturnPolicy =
        finalSale === "TRUE"
          ? {
              "@type": "MerchantReturnPolicy",
              applicableCountry: OFFER_TERMS.country,
              returnPolicyCategory:
                "https://schema.org/MerchantReturnNotPermitted",
            }
          : {
              "@type": "MerchantReturnPolicy",
              applicableCountry: OFFER_TERMS.country,
              returnPolicyCategory:
                "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: OFFER_TERMS.returnDays,
              returnMethod: "https://schema.org/ReturnByMail",
              returnFees: "https://schema.org/ReturnShippingFees",
              returnShippingFeesAmount: {
                "@type": "MonetaryAmount",
                value: OFFER_TERMS.returnShippingFee,
                currency: OFFER_TERMS.currency,
              },
            };
    }

    const regularAmount = parsePriceAmount(product.price);
    const saleAmount = parsePriceAmount(
      resolveProductPrice(product.special, null)
    );
    if (
      saleAmount &&
      regularAmount &&
      Number(regularAmount) > Number(saleAmount)
    ) {
      offer.priceSpecification = {
        "@type": "UnitPriceSpecification",
        priceType: "https://schema.org/StrikethroughPrice",
        price: regularAmount,
        priceCurrency: OFFER_TERMS.currency,
      };
    }

    node.offers = offer;
  }

  // Only emit ratings backed by real data.
  const reviews = Array.isArray(product.reviews) ? product.reviews : [];
  const ratingValue = Number(product.rating);
  if (Number.isFinite(ratingValue) && ratingValue > 0 && reviews.length > 0) {
    node.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(ratingValue),
      reviewCount: String(reviews.length),
    };
  }

  const authoredReviews = reviews.filter((r: ProductReviewSnapshot) =>
    Boolean(String(r?.author ?? "").trim())
  );
  if (authoredReviews.length > 0) {
    node.review = authoredReviews
      .slice(0, 10)
      .map((r: ProductReviewSnapshot) => {
        const review: Record<string, any> = {
          "@type": "Review",
          author: { "@type": "Person", name: String(r.author).trim() },
        };
        if (r.content) review.reviewBody = stripHtml(r.content).slice(0, 2000);
        if (r.created_at) review.datePublished = String(r.created_at);
        const rv = Number(r.rating);
        if (Number.isFinite(rv) && rv > 0) {
          review.reviewRating = {
            "@type": "Rating",
            ratingValue: String(rv),
            bestRating: "5",
          };
        }
        return review;
      });
  }

  return node;
}

type Crumb = { name: string; url: string };

type WebSiteJsonLdInput = {
  name: string;
  url: string;
  publisherId?: string;
  inLanguage?: string;
};

/**
 * Build the site identity node rendered once by the global layout. A
 * SearchAction is intentionally omitted: internal search is noindex and Google
 * retired the sitelinks search box, so advertising it would add no valid SEO
 * signal.
 */
export function buildWebSiteJsonLd(
  input: WebSiteJsonLdInput
): Record<string, any> | null {
  const name = String(input?.name || "").trim();
  const rawUrl = String(input?.url || "").trim();
  if (!name || !rawUrl) return null;

  let url = rawUrl;
  try {
    const parsed = new URL(rawUrl);
    parsed.hash = "";
    parsed.search = "";
    parsed.pathname = parsed.pathname.replace(/\/+$/, "") || "/";
    url = parsed.toString();
  } catch {
    return null;
  }

  const node: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${url}#website`,
    name,
    url,
  };
  if (input.publisherId) {
    node.publisher = { "@id": input.publisherId };
  }
  if (input.inLanguage) node.inLanguage = input.inLanguage;
  return node;
}

/**
 * Build a schema.org BreadcrumbList. Items must be ordered root -> current.
 */
export function buildBreadcrumbJsonLd(
  items: Crumb[]
): Record<string, any> | null {
  const valid = (items || []).filter((c) => c && c.name && c.url);
  if (valid.length < 2) return null;
  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: valid.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

type ListEntry = { url: string; name?: string };

type HierarchicalPageJsonLdInput = {
  url: string;
  name: string;
  breadcrumbName?: string;
  description?: string;
  pageType?: "WebPage" | "CollectionPage";
  homeName?: string;
  breadcrumbs?: ListEntry[];
};

function absoluteHttpUrl(value: unknown): string | null {
  try {
    const parsed = new URL(String(value || ""));
    if (!/^https?:$/.test(parsed.protocol)) return null;
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Build one root-to-current hierarchy for informational and collection pages.
 * Entity pages with deeper taxonomies continue to use buildBreadcrumbJsonLd
 * directly. Keeping these two nodes together prevents their names/URLs from
 * drifting while still rendering standard, independently typed JSON-LD.
 */
export function buildPageHierarchyJsonLd(
  input: HierarchicalPageJsonLdInput
): Record<string, any>[] | null {
  const url = absoluteHttpUrl(input?.url);
  const name = stripHtml(input?.name);
  const breadcrumbName = stripHtml(input?.breadcrumbName) || name;
  if (!url || !name || !breadcrumbName) return null;

  const parsed = new URL(url);
  const homeUrl = `${parsed.origin}/`;
  const pageId = `${url}#webpage`;
  const breadcrumbId = `${url}#breadcrumb`;
  const suppliedBreadcrumbs = (input?.breadcrumbs || [])
    .map((entry) => ({
      name: stripHtml(entry?.name),
      url: absoluteHttpUrl(entry?.url),
    }))
    .filter((entry): entry is { name: string; url: string } =>
      Boolean(entry.name && entry.url)
    );
  const suppliedLast = suppliedBreadcrumbs[suppliedBreadcrumbs.length - 1];
  let useSuppliedBreadcrumbs = false;
  if (suppliedBreadcrumbs.length >= 2 && suppliedLast) {
    try {
      const suppliedUrl = new URL(suppliedLast.url);
      const currentUrl = new URL(url);
      useSuppliedBreadcrumbs =
        suppliedUrl.origin === currentUrl.origin &&
        suppliedUrl.pathname === currentUrl.pathname;
    } catch {
      useSuppliedBreadcrumbs = false;
    }
  }
  const breadcrumbEntries = useSuppliedBreadcrumbs
    ? suppliedBreadcrumbs.map((entry, index) =>
        index === suppliedBreadcrumbs.length - 1
          ? { ...entry, url, name: entry.name || breadcrumbName }
          : entry
      )
    : [
        { name: stripHtml(input?.homeName) || "Home", url: homeUrl },
        { name: breadcrumbName, url },
      ];
  const breadcrumb = buildBreadcrumbJsonLd(breadcrumbEntries);
  if (!breadcrumb) return null;
  breadcrumb["@id"] = breadcrumbId;

  const page: Record<string, any> = {
    "@context": "https://schema.org/",
    "@type": input?.pageType || "WebPage",
    "@id": pageId,
    url,
    name,
    isPartOf: { "@id": `${homeUrl}#website` },
    breadcrumb: { "@id": breadcrumbId },
  };
  const description = stripHtml(input?.description);
  if (description) page.description = description.slice(0, 5000);

  return [page, breadcrumb];
}

/** Safely serialize JSON-LD for a raw <script> body during SSR. */
export function serializeJsonLd(value: unknown): string {
  if (!value) return "";
  try {
    return JSON.stringify(value).replace(/</g, "\\u003c");
  } catch {
    return "";
  }
}

/**
 * Build a schema.org ItemList of product URLs for a category/listing page.
 */
export function buildItemListJsonLd(
  entries: ListEntry[],
  listName?: string,
  startPosition = 1
): Record<string, any> | null {
  const valid = (entries || []).filter((e) => e && e.url);
  if (valid.length === 0) return null;
  const firstPosition = Math.max(1, Math.floor(Number(startPosition) || 1));
  const node: Record<string, any> = {
    "@context": "https://schema.org/",
    "@type": "ItemList",
    numberOfItems: valid.length,
    itemListElement: valid.map((e, i) => {
      const item: Record<string, any> = {
        "@type": "ListItem",
        position: firstPosition + i,
        url: e.url,
      };
      if (e.name) item.name = e.name;
      return item;
    }),
  };
  if (listName) node.name = listName;
  return node;
}
